/*
 * @author        david.loehr@zumper.com (David Loehr)
 * @copyright     Copyright (c) 2023, Zumper
 * @description   BottomHalf.tsx
 */

/** @jsx jsx */
import { Box } from '@chakra-ui/react'
import { ClassNames, jsx } from '@emotion/react'
import { useViewSection } from '@zcode-ui/hook'
import {
  AboutAddressContainerLazyLoadable,
  AmenitiesContainer,
  NearbyHomesLazySection,
  NearbyPetFriendlyLazySection,
  RentHistoryLazyLoadableSection,
  selectActiveListings,
  selectDecomposedAddress,
  selectMessageListingDetails,
} from '@zcode-ui/perm-address'
import {
  AffordabilityCalculatorLazyLoadable,
  DETAIL_CONTAINER_WIDTH,
  DetailContainer,
  DualScrollLayoutSimple,
  STICKY_NAV_LAYOUT_SHIFT,
  getMedianPricesByBedroom,
} from '@zcode-ui/web-zumper-features'
import { selectEntity, selectIsAddressOnBuilding } from '@zcode-ui/web-zumper-global-state'
import { MESSAGE_IMPRESSION, RENT_HISTORY_IMPRESSION } from '@zcode-ui/web-zumper-integrations'
import { LazyHydratedSection, ScrollingNavSection } from '@zcode-ui/web-zumper-ui'
import { DETAIL, PA } from '@zcode/mixpanel'
import { AnalyticsC, Origin } from '@zcode/nr-utils'
import { MESSAGE_FORM_INLINE } from '@zcode/nr/es/shared/constants/message'
import type { MaybeEmptyProperty } from '@zcode/state-type'
import React, { Fragment, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { DetailSchoolWidgetLazyLoadableSection } from '../../../components/DetailSchoolWidget/DetailSchoolWidgetLoadableSection'
import { LocalLogicLazyLoadableSection } from '../../../components/LocalLogic/LocalLogicLoadableSection'
import { InactiveSaveSearchStickyContainer } from '../../../components/SaveSearch/InactiveSaveSearchSection/InactiveSaveSearchSticky/InactiveSaveSearchStickyContainer'
import { DetailAds } from '../../../components/detail/DetailAds/DetailAds'
import { ProsAndConsLazyLoadable } from '../../../components/detail/ProsAndCons/ProsAndConsLazyLoadable'
import { useShouldRenderAddressProsAndCons } from '../../../components/detail/ProsAndCons/useShouldRenderAddressProsAndCons'
import { DetailRenterInfoLazyLoadable } from '../../../components/detail/RenterInfo/DetailRenterInfoLazyLoadable'
import { FooterLazyLoadableSection } from '../../../components/footer/FooterLazyLoadable'
import { MessageFormLoadable } from '../../../components/message/MessageForm/MessageFormLoadable'
import { selectShouldLimitLeads } from '../../../components/message/MessageForm/helpers/getShouldLimitLeads'
import {
  RelatedPropertiesLazyLoadableSection,
  RelatedPropertiesLoadingFallback,
} from '../../../features/RelatedProperties/RelatedPropertiesLazyLoadableSection'
import { selectBreadcrumb } from '../../../modules/currentSearch/modules/location/selectors/index'
import { selectEntityFetching } from '../../../modules/detail/modules/entity/selectors/index'
import { trackMessageImpression as _trackMessageImpression } from '../../../modules/traktor/thunks/trackMessageImpression'
import { useDispatch } from '../../../modules/useDispatch'
import { getHoodAndCityLinks } from '../../PageLTRDetail/helpers/getHoodAndCityLinks'
import { useHigherEducation } from '../../PageLTRDetail/hooks/useHigherEducation'
import { AddressRentTrendsLazySection } from '../AddressRentTrends/AddressRentTrendsLazySection'
import { LinkPackLazyLoadableSection } from '../LinkPack/LinkPackLazyLoadableSection'
import { MessageFormContainer } from '../MessageForm/MessageFormContainer'
import { trackViewPASection } from '../helpers/trackViewPASection'
import { AddressPoliciesAndFeesLazyLoadable } from './AddressPoliciesAndFeesLazyLoadable'
import * as styles from './styles'

const defaultRootMargin = '1000px' // very generous root margin to load chunks earlier and prevent CLS

export const BottomHalf = () => {
  const entity: MaybeEmptyProperty = useSelector(selectEntity) ?? {}
  const activeListings = useSelector(selectActiveListings)
  const breadcrumb = useSelector(selectBreadcrumb)
  const dispatch = useDispatch()
  const isLoading = useSelector(selectEntityFetching)
  const shouldLimitLeads = useSelector(selectShouldLimitLeads)
  const messageListing = useSelector(selectMessageListingDetails)
  const hoodAndCityLinks = getHoodAndCityLinks(entity, true)
  const decomposedAddress = useSelector(selectDecomposedAddress)
  const hasAvailableListings = !!activeListings.length
  const bedroomPrices = useMemo(() => getMedianPricesByBedroom(activeListings), [activeListings])
  const shortAddress = decomposedAddress.shortAddress
  const shouldRenderAffCalculator = !!shortAddress && Object.keys(bedroomPrices).length > 0
  const shouldRenderSchoolsEarlier = useHigherEducation()
  const shouldRenderAddressProsAndCons = useShouldRenderAddressProsAndCons()
  const isAddressOnBuilding = useSelector(selectIsAddressOnBuilding)

  const aboutRef = useViewSection(
    useCallback(() => {
      dispatch(
        trackViewPASection({ traktorName: 'DETAIL_ABOUT_IMPRESSION', mixpanelName: DETAIL.ABOUT })
      )
    }, [dispatch])
  )
  const amenitiesRef = useViewSection(
    useCallback(() => {
      dispatch(
        trackViewPASection({
          traktorName: 'DETAIL_AMENITIES_IMPRESSION',
          mixpanelName: DETAIL.AMENITIES,
        })
      )
    }, [dispatch])
  )
  const msgFormRef = useViewSection(
    useCallback(() => {
      dispatch(
        trackViewPASection({ traktorName: MESSAGE_IMPRESSION, mixpanelName: PA.MESSAGE_FORM })
      )
    }, [dispatch])
  )
  const rentHistoryRef = useViewSection(
    useCallback(() => {
      dispatch(
        trackViewPASection({
          traktorName: RENT_HISTORY_IMPRESSION,
          mixpanelName: DETAIL.RENT_HISTORY,
        })
      )
    }, [dispatch])
  )
  const trackMessageImpression = useCallback(
    (...args: Parameters<typeof _trackMessageImpression>) =>
      dispatch(_trackMessageImpression(...args)),
    [dispatch]
  )

  return (
    <React.Fragment>
      <Box<'div'> ml="auto" mr="auto" mt={{ base: 16, lg: 20 }} width={DETAIL_CONTAINER_WIDTH}>
        <DualScrollLayoutSimple
          showSlotSticky={!shouldLimitLeads}
          slotBody={
            <React.Fragment>
              <AmenitiesContainer
                ref={amenitiesRef}
                shortAddress={decomposedAddress.shortAddress}
              />
              <ScrollingNavSection name="Policies">
                <AddressPoliciesAndFeesLazyLoadable rootMargin="200px 200px" />
                <Box<'div'> display={{ lg: 'none' }} mt={16}>
                  <DetailAds layout="square" showBorderBottom showBorderTop />
                </Box>
              </ScrollingNavSection>
              <ScrollingNavSection mt={20} name="About">
                <div ref={aboutRef}>
                  <AboutAddressContainerLazyLoadable
                    breadcrumb={breadcrumb}
                    hoodAndCityLinks={hoodAndCityLinks}
                    rootMargin="200px 200px"
                  />
                </div>
                {shouldRenderSchoolsEarlier && (
                  <DetailSchoolWidgetLazyLoadableSection isHigherEducationExperiment />
                )}
                {shouldRenderAddressProsAndCons && <ProsAndConsLazyLoadable />}
                <DetailRenterInfoLazyLoadable mb={20} rootMargin="200px 200px" />
                {hasAvailableListings && (
                  <AddressRentTrendsLazySection
                    analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
                    rootMargin={defaultRootMargin}
                  />
                )}
                {shouldRenderAffCalculator ? (
                  <AffordabilityCalculatorLazyLoadable
                    location={shortAddress}
                    bedroomPrices={bedroomPrices}
                  />
                ) : null}
              </ScrollingNavSection>
            </React.Fragment>
          }
          slotSticky={
            hasAvailableListings ? (
              entity.isMessageable ? (
                <Box<'div'> display={{ base: 'none', lg: 'block' }}>
                  <div ref={msgFormRef}>
                    <MessageFormContainer />
                  </div>
                </Box>
              ) : null
            ) : (
              <Box<'div'> display={{ base: 'none', lg: 'block' }}>
                <LazyHydratedSection>
                  <InactiveSaveSearchStickyContainer
                    header="We’ll let you know when units at this address become available and send you similar listings."
                    origin={
                      isAddressOnBuilding
                        ? Origin.ADDR_ADDRESS_PAGE_INLINE_ALERT
                        : Origin.ADDRESS_PAGE_INLINE_ALERT
                    }
                  />
                </LazyHydratedSection>
              </Box>
            )
          }
          stickyNavLayoutShift="both"
          stickyPadding={false}
          stickyTop="calc(var(--chakra-space-14) + 1px * var(--header-height, 70))"
        />
      </Box>
      <Box<'div'> {...STICKY_NAV_LAYOUT_SHIFT}>
        {!hasAvailableListings && (
          <DetailContainer>
            {isLoading ? (
              <RelatedPropertiesLoadingFallback />
            ) : (
              <RelatedPropertiesLazyLoadableSection
                analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
                origin={
                  isAddressOnBuilding
                    ? Origin.ADDR_DETAIL_MM_RELATED_PROPERTIES
                    : Origin.DETAIL_MM_RELATED_PROPERTIES
                }
                rootMargin={defaultRootMargin}
              />
            )}
            <AddressRentTrendsLazySection
              analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
              rootMargin={defaultRootMargin}
              copyContainerProps={{ maxW: { base: '100%', md: '75%' } }}
            />
          </DetailContainer>
        )}
        <ScrollingNavSection continuationId="Affordability Calculator">
          <DetailContainer>
            <div ref={rentHistoryRef}>
              <RentHistoryLazyLoadableSection rootMargin={defaultRootMargin} shouldExpand />
            </div>
          </DetailContainer>
        </ScrollingNavSection>

        <ScrollingNavSection name="Neighborhood">
          <DetailContainer>
            {!shouldRenderSchoolsEarlier && (
              <DetailSchoolWidgetLazyLoadableSection isHigherEducationExperiment={false} />
            )}
            <DetailAds layout="leaderboard" showBorderTop />
          </DetailContainer>
          <Box<'div'> bg={{ base: 'none', md: 'gray.50' }}>
            <DetailContainer>
              <LocalLogicLazyLoadableSection
                localLogicCss={styles.localLogic}
                poiCss={styles.poi}
                rootMargin={defaultRootMargin}
              />
            </DetailContainer>
          </Box>

          <DetailContainer>
            {hasAvailableListings && (
              <Fragment>
                {isLoading ? (
                  <RelatedPropertiesLoadingFallback />
                ) : (
                  <RelatedPropertiesLazyLoadableSection
                    analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
                    origin={
                      isAddressOnBuilding
                        ? Origin.ADDR_DETAIL_MM_RELATED_PROPERTIES
                        : Origin.DETAIL_MM_RELATED_PROPERTIES
                    }
                    rootMargin={defaultRootMargin}
                  />
                )}
              </Fragment>
            )}
            <Box<'div'> display={{ base: 'block', md: 'none' }} mt="4">
              {hasAvailableListings && messageListing ? (
                entity.isMessageable ? (
                  <ClassNames>
                    {({ css }) => (
                      <MessageFormLoadable
                        allowMFTourButton
                        analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
                        containerClass={css(
                          `padding: 7px 0 32px;
                      border: none;
                      box-shadow: none;`
                        )}
                        ctaName="Check availability"
                        entity={messageListing}
                        formType={MESSAGE_FORM_INLINE}
                        headerText="Check availability"
                        hideCancelButton
                        hideCustomMessage
                        hideMoveInDate
                        onView={trackMessageImpression}
                        origin={
                          isAddressOnBuilding
                            ? Origin.ADDR_DETAIL_MOBILE_BOTTOM
                            : Origin.DETAIL_MOBILE_BOTTOM
                        }
                        rootMargin={defaultRootMargin}
                        showSummary={false}
                        unavailableMsgClass={css('padding: 0')}
                      />
                    )}
                  </ClassNames>
                ) : null
              ) : (
                <InactiveSaveSearchStickyContainer
                  header="We’ll let you know when units at this address become available and send you similar listings."
                  origin={
                    isAddressOnBuilding
                      ? Origin.ADDR_DETAIL_MOBILE_BOTTOM
                      : Origin.DETAIL_MOBILE_BOTTOM
                  }
                />
              )}
            </Box>
          </DetailContainer>

          <DetailContainer mb={20}>
            <LinkPackLazyLoadableSection rootMargin={defaultRootMargin} />
          </DetailContainer>

          <DetailContainer mb={20}>
            <NearbyPetFriendlyLazySection rootMargin={defaultRootMargin} />
          </DetailContainer>

          <DetailContainer mb={20}>
            <NearbyHomesLazySection rootMargin={defaultRootMargin} />
          </DetailContainer>

          <DetailContainer mb={20}>
            <Box<'div'> display={{ base: 'none', lg: 'block' }}>
              <DetailAds layout="twoSquare" showBorderBottom />
            </Box>
            <Box<'div'> display={{ lg: 'none' }}>
              <DetailAds adType={2} layout="square" />
            </Box>
          </DetailContainer>

          <Box<'div'> bg="#25323e">
            <Box<'div'>
              mx={{ base: 6, md: 'auto', lg: 8, '2xl': 'auto' }}
              w={{ base: 'initial', md: '719px', lg: 'initial', '2xl': '1376px' }}
            >
              <FooterLazyLoadableSection
                analyticChannel={AnalyticsC.PERMANENT_ADDRESS}
                rootMargin={defaultRootMargin}
              />
            </Box>
          </Box>
        </ScrollingNavSection>
      </Box>
    </React.Fragment>
  )
}
